import { Injectable, signal, computed } from '@angular/core';
import { PosNumberItem } from '../types/numeros-pos.model';
import { PosBalance } from '../types/pos-balance.model';

/**
 * Servicio de estado compartido para los números POS del usuario en sesión.
 * Actúa como fuente de verdad entre main-layout, numeros-pos y crear-pos.
 */
@Injectable({
  providedIn: 'root'
})
export class PosStateService {
  private _posNumbers = signal<PosNumberItem[]>([]);
  private _selectedPos = signal<PosNumberItem | null>(null);

  /** Saldo del POS actualmente seleccionado */
  private _balance = signal<PosBalance | null>(null);

  /** Lista reactiva de POS (solo activos para el dropdown del header) */
  readonly posNumbers = computed(() => this._posNumbers().filter(p => p.active));

  /** POS actualmente seleccionado en el header */
  readonly selectedPos = computed(() => this._selectedPos());

  /** Saldo del POS como signal reactivo, disponible para cualquier componente */
  readonly balance = this._balance.asReadonly();

  /** Monto de saldo disponible */
  readonly saldoDisponible = computed(() => this._balance()?.amount ?? 0);

  /**
   * Inicializa el estado desde localStorage al arrancar la sesión.
   * Llamado por MainLayoutComponent en ngOnInit.
   */
  init(pos: PosNumberItem[]): void {
    this._posNumbers.set(pos);
    const defaultPos = pos.find(p => p.default && p.active) ?? pos.find(p => p.active) ?? null;
    this._selectedPos.set(defaultPos);
    if (defaultPos) localStorage.setItem('selectedPOS', defaultPos.number);
  }

  /** Selecciona un POS desde el dropdown del header */
  select(pos: PosNumberItem): void {
    this._selectedPos.set(pos);
    localStorage.setItem('selectedPOS', pos.number);
  }

  /** Agrega un nuevo POS a la lista (llamado tras crear exitosamente) */
  add(pos: PosNumberItem): void {
    this._posNumbers.update(list => [...list, pos]);
    if (pos.default || this.posNumbers().length === 1) {
      this._selectedPos.set(pos);
      localStorage.setItem('selectedPOS', pos.number);
    }
  }

  /** Actualiza un POS existente en la lista (llamado tras modificar) */
  update(updated: PosNumberItem): void {
    this._posNumbers.update(list =>
      list.map(p => p.number === updated.number ? updated : p)
    );
    if (this._selectedPos()?.number === updated.number) {
      const stillActive = updated.active ? updated : null;
      this._selectedPos.set(stillActive);
      if (stillActive) localStorage.setItem('selectedPOS', stillActive.number);
    }
    if (updated.default && updated.active) {
      this._selectedPos.set(updated);
      localStorage.setItem('selectedPOS', updated.number);
    }
  }

  /** Marca un POS como inactivo (soft-delete) y reajusta la selección */
  remove(deleted: PosNumberItem): void {
    this._posNumbers.update(list =>
      list.map(p => p.number === deleted.number ? deleted : p)
    );
    if (this._selectedPos()?.number === deleted.number) {
      const nextActive = this._posNumbers().find(p => p.active && p.number !== deleted.number) ?? null;
      this._selectedPos.set(nextActive);
      if (nextActive) localStorage.setItem('selectedPOS', nextActive.number);
      else localStorage.removeItem('selectedPOS');
    }
  }

  /** Limpia el estado al cerrar sesión */
  clear(): void {
    this._posNumbers.set([]);
    this._selectedPos.set(null);
    this._balance.set(null);
    localStorage.removeItem('selectedPOS');
  }

  /** Actualiza el saldo del POS tras consultarlo */
  setBalance(balance: PosBalance): void {
    this._balance.set(balance);
  }
}
